import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MatchWeather {
  temperature: number;
  apparent: number;
  precipitationProbability: number;
  windSpeed: number;
  windGusts: number;
  code: number;
}

export type WeatherKind = 'clear' | 'partly' | 'cloudy' | 'fog' | 'rain' | 'snow' | 'storm';

export function weatherKind(code: number): WeatherKind {
  if (code === 0 || code === 1) return 'clear';
  if (code === 2) return 'partly';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 95) return 'storm';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
  return 'rain';
}

const FORECAST_DAYS = 14;

/** Forecast for the club's coordinates at the hour the match starts. Null when unavailable. */
export function useMatchWeather(clubId: string | undefined | null, matchDate: Date) {
  const msAhead = matchDate.getTime() - Date.now();
  const inWindow = msAhead > -2 * 3600_000 && msAhead < FORECAST_DAYS * 86400_000;

  const coords = useQuery({
    queryKey: ['club-coords', clubId],
    enabled: !!clubId && inWindow,
    staleTime: 24 * 3600_000,
    queryFn: async () => {
      const { data } = await supabase.from('padel_clubs').select('lat, lon').eq('id', clubId!).maybeSingle();
      const lat = (data as any)?.lat, lon = (data as any)?.lon;
      return lat != null && lon != null ? { lat: Number(lat), lon: Number(lon) } : null;
    },
  });

  const hourKey = new Date(matchDate);
  hourKey.setMinutes(0, 0, 0);

  return useQuery({
    queryKey: ['match-weather', coords.data?.lat, coords.data?.lon, hourKey.toISOString()],
    enabled: !!coords.data && inWindow,
    staleTime: 30 * 60_000,
    retry: 1,
    queryFn: async (): Promise<MatchWeather | null> => {
      const { lat, lon } = coords.data!;
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
        `&hourly=temperature_2m,apparent_temperature,precipitation_probability,weather_code,wind_speed_10m,wind_gusts_10m` +
        `&timezone=UTC&forecast_days=${FORECAST_DAYS}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const json = await res.json();
      const times: string[] = json?.hourly?.time ?? [];
      const target = hourKey.toISOString().slice(0, 13); // YYYY-MM-DDTHH
      const i = times.findIndex((t) => t.slice(0, 13) === target);
      if (i < 0) return null;
      const h = json.hourly;
      return {
        temperature: Math.round(h.temperature_2m[i]),
        apparent: Math.round(h.apparent_temperature[i]),
        precipitationProbability: h.precipitation_probability[i] ?? 0,
        windSpeed: Math.round(h.wind_speed_10m[i]),
        windGusts: Math.round(h.wind_gusts_10m[i]),
        code: h.weather_code[i],
      };
    },
  });
}
